import clsx from "clsx";
import DomPurify from "isomorphic-dompurify";
import Image from "next/image";
import Link from "next/link";

type OptionSectionProps = {
  link?: string;
  title: string;
  subTitle?: string;
  body: string;
  image?: string;
  bgColor?: string;
  position?: "left" | "center" | "right";
  keyPoints?: string[]; // New prop for key points
};

const OptionSection = ({
  link,
  title,
  subTitle = "",
  body,
  image,
  position,
  keyPoints,
  bgColor
}: OptionSectionProps) => {
  const section = (
    <article
      className={clsx("mb-16 flex flex-1 flex-col rounded-2xl px-2 py-8 md:px-8 md:py-10", {
        ["items-center"]: !position || position === "center",
        ["items-start"]: position === "left",
        ["items-end"]: position === "right"
      })}
      style={{ backgroundColor: bgColor }}
    >
      {/* Conditionally render the image div */}
      {image && (
        <div className="relative aspect-square h-full w-full">
          <Image src={image} fill alt={title} />
        </div>
      )}
      <div
        className={clsx("mb-4 flex flex-col px-6 lg:px-0", {
          ["items-center text-center"]: !position || position === "center",
          ["items-start text-left"]: position === "left",
          ["items-end text-right"]: position === "right"
        })}
      >
        {subTitle && <p className="mt-3 whitespace-pre-wrap text-md font-semibold">{subTitle}</p>}
        <h3
          className={clsx("text-accent-700 two-lines my-4 px-4 font-heading text-3xlt")}
          dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title) }}
        />

        <p className="mb-4 mt-2 max-w-[275px] text-md font-light italic text-primary-700">{body}</p>

        {/* Rendering key points */}
        {keyPoints && keyPoints.length > 0 && (
          <ul className="mt-4 list-inside list-decimal text-center">
            {keyPoints.map((point, index) => (
              <li key={index} className="list-none py-2">
                {point}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );

  if (link) {
    return <Link href={link}>{section}</Link>;
  }

  return section;
};

export default OptionSection;
